import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { fetchers } from "../lib/api";
import LeagueTabs from "../components/LeagueTabs";
import AdSlot from "../components/AdSlot";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

const Info = ({ label, value }) => (
  <div className="border border-border bg-background/60 p-3">
    <div className="overline-eyebrow text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-xl">{value || "—"}</div>
  </div>
);

export default function Teams() {
  const [params] = useSearchParams();
  const [league, setLeague] = useState(params.get("league") || "epl");
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["teams", league], queryFn: () => fetchers.teams(league) });
  const { data: detail } = useQuery({
    queryKey: ["team", league, selected],
    queryFn: () => fetchers.team(selected, league),
    enabled: !!selected,
  });
  const teams = data?.teams || [];

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-6">
        <div className="overline-eyebrow text-primary">Clubs</div>
        <h1 className="font-display text-5xl uppercase leading-none sm:text-6xl">Team Profiles</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">Browse clubs from 20 competitions. Tap a badge for a quick dossier.</p>
      </div>

      <AdSlot size="leaderboard" slotId="teams-top" className="mb-8 hidden md:flex" />
      <AdSlot size="large_mobile" slotId="teams-top-m" className="mb-8 md:hidden" />

      <LeagueTabs value={league} onChange={setLeague} />

      <div className="mt-8 grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {isLoading && <div className="col-span-full text-sm text-muted-foreground">Loading teams…</div>}
        {teams.map((t) => (
          <button
            key={t.id}
            data-testid={`team-card-${t.id}`}
            onClick={() => setSelected(t.id)}
            className="card-hover group flex items-center gap-4 border border-border bg-card p-4 text-left hover:border-primary"
            style={{ borderLeft: t.color ? `4px solid #${t.color}` : undefined }}
          >
            {t.logo && <img src={t.logo} alt={t.name} className="h-14 w-14 shrink-0 object-contain transition-transform group-hover:scale-105" loading="lazy" />}
            <div className="min-w-0">
              <div className="truncate font-display text-xl uppercase group-hover:text-primary">{t.name}</div>
              {t.location && t.location !== t.name && (
                <div className="truncate text-xs uppercase tracking-wider text-muted-foreground">{t.location}</div>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="my-10">
        <AdSlot size="billboard" slotId="teams-bottom" className="hidden lg:flex" />
        <AdSlot size="responsive" slotId="teams-bottom-m" className="lg:hidden" />
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl border-border bg-card" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 font-display text-3xl uppercase">
              {detail?.logo && <img src={detail.logo} alt="" className="h-10 w-10 object-contain" />}
              {detail?.name || "Loading…"}
            </DialogTitle>
            <DialogDescription>{detail?.league}</DialogDescription>
          </DialogHeader>
          {detail && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Position" value={detail.standing} />
                <Info label="Record (W-D-L)" value={detail.record} />
                <Info label="Next Match" value={detail.nextMatch} />
                <Info label="Kick-off" value={detail.nextDate && new Date(detail.nextDate).toLocaleString()} />
              </div>
              <AdSlot size="responsive" slotId="team-modal" className="mt-4" />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
